// ── permissions.js — V50 매니저 권한 카테고리 (UI 강제) ─────────────────
//
// 권한 모델:
//   매니저 doc(`/users/{ownerUid}/managers/{managerUid}`)의 `permissions` 필드
//     { transactions: 'write'|'read'|'none', purchaseLog: ..., ... }
//
// 강제 위치:
//   - Firestore Rules는 V49 그대로 (owner OR manager 둘 다 read/create/update)
//   - 베타라 client에서 메뉴 숨김 + 버튼 disable로 강제
//   - 매니저가 콘솔 만지면 우회 가능하지만 베타 단계에선 OK
//
// 레벨 의미:
//   'none'  — 메뉴 자체 숨김. 데이터 fetch도 안 함
//   'read'  — 조회만, 입력·수정 버튼 disable
//   'write' — 입력·수정 가능. 삭제는 어쨌든 owner 전용 (Rules)

// 카테고리 정의 — 앱 메뉴 구조 기반
export const PERMISSION_CATEGORIES = [
  { key: 'transactions', label: '거래장부',         icon: '📒', desc: '매출·매입 입력 / 조회' },
  { key: 'purchaseLog',  label: '매입·영수증 OCR',  icon: '🧾', desc: '영수증 사진 / 매입 메모' },
  { key: 'priceList',    label: '단가표·메뉴 가격',  icon: '💰', desc: '식자재 단가 / 메뉴 가격 결정' },
  { key: 'recipes',      label: '레시피',           icon: '📖', desc: '메뉴 레시피·원가 분석' },
  { key: 'inventory',    label: '재고',             icon: '📦', desc: '재고 입력·실사' },
  { key: 'orderHistory', label: '발주',             icon: '🛒', desc: '발주 내역 / AI 발주 추천' },
  { key: 'payroll',      label: '직원·급여',        icon: '👥', desc: '직원·스케줄·인건비 (민감 정보)' },
  { key: 'tax',          label: '세무',             icon: '🧮', desc: '부가세·종소세 (민감 정보)' },
  { key: 'operations',   label: '대시보드·손익',     icon: '📊', desc: '운영 대시보드 / 손익 분석' },
];

export const PERMISSION_LEVELS = ['none', 'read', 'write'];
export const LEVEL_LABEL = { none: '없음', read: '읽기', write: '입력' };
export const LEVEL_COLOR = {
  none:  { bg: '#F4F4F5', fg: '#52525B', border: '#fca5a5' },
  read:  { bg: '#F4F4F5', fg: '#52525B', border: '#D4D4D8' },
  write: { bg: '#F4F4F5', fg: '#9A3412', border: '#FED7AA' },
};

// 프리셋 4종 — 빠른 선택용
export const PERMISSION_PRESETS = {
  full: {
    label: '풀 매니저', icon: '🟢',
    desc: '결제·플랜 빼고 사장 권한 거의 동일',
    permissions: {
      transactions: 'write', purchaseLog: 'write', priceList: 'write',
      recipes: 'write', inventory: 'write', orderHistory: 'write',
      payroll: 'write', tax: 'write', operations: 'write',
    },
  },
  store: {
    label: '매장 매니저', icon: '🔵',
    desc: '거래·재고·발주만. 직원·세무는 차단',
    permissions: {
      transactions: 'write', purchaseLog: 'write', priceList: 'read',
      recipes: 'read', inventory: 'write', orderHistory: 'write',
      payroll: 'none', tax: 'none', operations: 'read',
    },
  },
  accounting: {
    label: '회계 보조', icon: '🟡',
    desc: '거래·매입·세무 입력. 메뉴·재고·직원은 차단',
    permissions: {
      transactions: 'write', purchaseLog: 'write', priceList: 'read',
      recipes: 'none', inventory: 'none', orderHistory: 'read',
      payroll: 'none', tax: 'write', operations: 'read',
    },
  },
  viewer: {
    label: '읽기 전용', icon: '⚪',
    desc: '모든 데이터 조회만. 입력 불가',
    permissions: {
      transactions: 'read', purchaseLog: 'read', priceList: 'read',
      recipes: 'read', inventory: 'read', orderHistory: 'read',
      payroll: 'none', tax: 'read', operations: 'read',
    },
  },
};

// 기본 권한 — 풀 매니저 (가장 사용 많은 패턴)
export const DEFAULT_PERMISSIONS = PERMISSION_PRESETS.full.permissions;

// permissions 객체 normalize — 누락된 카테고리는 'none'으로 채움
export function normalizePermissions(perms) {
  const out = {};
  for (const cat of PERMISSION_CATEGORIES) {
    const v = perms && perms[cat.key];
    out[cat.key] = PERMISSION_LEVELS.includes(v) ? v : 'none';
  }
  return out;
}

// 권한 체크 헬퍼
//   has('read', cat) — 해당 카테고리를 최소 'read' 이상 가졌는지
//   has('write', cat) — 'write'를 가졌는지
export function makePermissionChecker(perms) {
  const norm = normalizePermissions(perms);
  return (need, cat) => {
    const cur = norm[cat] || 'none';
    if (need === 'read')  return cur === 'read' || cur === 'write';
    if (need === 'write') return cur === 'write';
    if (need === 'none')  return true;
    return false;
  };
}

// 현재 프리셋 추측 (UI 표시용)
export function detectPreset(perms) {
  const norm = JSON.stringify(normalizePermissions(perms));
  for (const [key, preset] of Object.entries(PERMISSION_PRESETS)) {
    if (JSON.stringify(normalizePermissions(preset.permissions)) === norm) return key;
  }
  return 'custom';
}
